// Consumes the `reportGeneration` launch handle's `polling_url` + `job_id`
// (dropped by the read-only lib/skipper poll) to track a guided-report generation
// to its terminal state in-session. Every tick carries the persisted report object
// alongside the state, so the caller can swap the placeholder for the real report
// instead of waiting for a reload. Mirrors the stat-server job poll shape used by
// ai_assistants.coffee / comments/apiCalls.js — the request is authenticated with
// the current StatApiKey (`?key=` + body) and identifies the job by `job_id`.
import { schedulePoll } from '@aytm/app/stats/stores/pollDelay'
import { sendJobJSONData } from '@aytm/app/stats/lib/io/fetch'

// Generation runs ~5–10 min, so a slow cadence matching the chat poll is fine.
const POLL_MS = 15000

// The stat server's enum says ERROR, the aytm side says 'errored' (persisted generation state, and
// what the report-status poll answers). Either spelling can reach the bag, so both are terminal.
const FAILURE_STATES = ['ERROR', 'ERRORED']

// The StatApiKey (data-scope key) that authenticates stat-server job requests —
// the same value the stats chat assistant surfaces as its context token. Re-read
// each tick so a mid-generation key refresh is picked up. Null while invalid.
const currentApiKey = (): string | null => {
  const model = (window as unknown as { StatPage?: { model?: StatPageModel } }).StatPage?.model
  if (!model?.getApiKey || model.isApiKeyInvalid?.()) return null
  return model.getApiKey()?.code ?? null
}

interface StatPageModel {
  getApiKey?: () => { code?: string } | null
  isApiKeyInvalid?: () => boolean
}

const withKey = (url: string, key: string): string =>
  `${url}${url.includes('?') ? '&' : '?'}key=${encodeURIComponent(key)}`

export interface GuidedReportJobPoll {
  start(): void
  stop(): void
}

// The generation bag on the poll: `state` is the report lifecycle token (IN_PROGRESS / READY /
// ERROR) and `report` the report object the save returned — the same shape as one entry of the
// page's report list.
export interface ReportGenerationBag {
  state?: string
  reportId?: number
  generationId?: string
  report?: Record<string, unknown>
  // Set when a newer turn won the chat session: the losing job still runs to completion and
  // says so here, deliberately carrying no `state` — nothing failed and nothing was written.
  casConflict?: boolean
  cas_conflict?: boolean
}

// A completed job answers with the job's return value (bag nested under `scope`); every other
// state answers with the job's progressData bag.
interface JobPollPayload {
  status?: string
  result?: {
    state?: string
    pollingUrl?: string
    data?: ReportGenerationBag & { scope?: ReportGenerationBag }
  }
}

interface GuidedReportGenerationHandle {
  generation_id?: string
  job_id?: string
  polling_url?: string
  dispatched_at?: string
}

// Matches the backend's INFLIGHT_GUARDRAIL / STUCK_THRESHOLD (60 min): a dispatch
// older than this is treated as no longer in-flight.
const INFLIGHT_WINDOW_MS = 60 * 60 * 1000

// Stable identity of a generation launch handle, or null when it carries no
// pollable address. The caller compares this against the handle it is already
// tracking so an unchanged handle received again does not restart the poll —
// only a different generation (a different key) starts a fresh one.
export const generationPollKey = (generation?: GuidedReportGenerationHandle): string | null => {
  if (!generation?.polling_url) return null
  return generation.generation_id ?? generation.job_id ?? generation.polling_url
}

// Whether a launch handle represents a still-in-flight generation. The stored
// handle is never cleared on the backend, so its mere presence is not a signal —
// only a dispatch within the in-flight window counts. Guards the fire condition
// so a stale handle (a long-completed generation) never materialises a draft.
export const isGenerationHandleFresh = (generation?: GuidedReportGenerationHandle, nowMs = Date.now()): boolean => {
  const dispatchedAt = generation?.dispatched_at
  if (!dispatchedAt) return false
  const t = Date.parse(dispatchedAt)
  if (Number.isNaN(t)) return false
  return nowMs - t <= INFLIGHT_WINDOW_MS
}

// Fires `onReady` once when the generation reports READY, or `onError` once on a terminal
// failure, then stops itself. `onSuperseded` fires instead when a newer turn won the session:
// the row must be left exactly as it is for the winning generation to settle. `onReport` fires
// once before any of those, on the first still-running tick carrying the persisted report. The
// caller must also stop() on teardown in case the job never terminates.
export const createGuidedReportJobPoll = ({
  pollingUrl,
  jobId,
  onReady,
  onError,
  onReport,
  onSuperseded
}: {
  pollingUrl: string
  jobId?: string
  onReady: (report?: Record<string, unknown>) => void
  onError: (reason: string) => void
  onReport?: (report: Record<string, unknown>) => void
  onSuperseded?: () => void
}): GuidedReportJobPoll => {
  let cancelWait: (() => void) | null = null
  let controller: AbortController | null = null
  let stopped = false
  let done = false
  let reported = false
  // The stat server may hand back a fresh poll URL each tick — follow it.
  let baseUrl = pollingUrl

  const stop = (): void => {
    stopped = true
    cancelWait?.()
    cancelWait = null
    controller?.abort()
  }

  const schedule = (): void => {
    if (stopped) return
    cancelWait = schedulePoll(POLL_MS, () => void poll())
  }

  const poll = async (): Promise<void> => {
    if (stopped || done) return

    const key = currentApiKey()
    // No valid data-scope key yet (not fetched / expired) — retry next tick.
    if (!key) return schedule()

    controller = new AbortController()
    try {
      const data: JobPollPayload = await sendJobJSONData({
        url: withKey(baseUrl, key),
        method: 'POST',
        // Mirrors the stat-server job poll (ai_assistants.coffee / comments/apiCalls.js):
        // `key` = data-scope StatApiKey, `token` = the job id from the launch handle.
        data: { key, token: jobId },
        signal: controller.signal
      })
      // Envelope status + the generation bag (state IN_PROGRESS / READY / ERROR, plus the
      // report the reported save persisted). Coerced to a string so a tick without a state
      // compares as absent rather than throwing.
      const status = String(data?.status ?? '').toUpperCase()
      const state = String(data?.result?.state ?? '').toUpperCase()
      const bag = data?.result?.data ?? {}
      const generation = bag.scope ?? bag
      const generationState = String(generation.state ?? '').toUpperCase()
      if (data?.result?.pollingUrl) baseUrl = data.result.pollingUrl

      // A newer turn won the session and this job lost it. Nothing failed and nothing was
      // written, so neither a failure nor a result may be rendered: stand down and leave the row
      // to the winning generation, releasing the claim so it can be watched in this one's place.
      if (generation.casConflict || generation.cas_conflict) {
        done = true
        onSuperseded?.()
        return stop()
      }

      if (
        ['FAIL', 'NOT FOUND'].includes(status) ||
        FAILURE_STATES.includes(state) ||
        FAILURE_STATES.includes(generationState)
      ) {
        done = true
        onError(generationState || state || status || 'unknown')
        return stop()
      }
      // READY is the report-generation enum; COMPLETED is the job's own terminal state, which is
      // what the legacy assistants poll keys off on this same endpoint. The generation's own state
      // outranks it: the job can answer COMPLETED while the generation it carries is still
      // IN_PROGRESS, so COMPLETED settles the row only when the generation reports no state at all
      // — which is what keeps the spinner from hanging to the guardrail on a stateless result.
      if ('READY' === generationState || ('COMPLETED' === state && !generationState)) {
        done = true
        onReady(generation.report)
        return stop()
      }
      // Still running, and the row the stat server persisted at launch is on the wire: hand it
      // over once so the placeholder becomes the real report before the generation finishes.
      if (!reported && generation.report) {
        reported = true
        onReport?.(generation.report)
      }
    } catch {
      // Transient (abort / network) — keep polling until stopped.
    }
    schedule()
  }

  return {
    start(): void {
      if (!stopped && null === cancelWait) void poll()
    },
    stop
  }
}
